import React, { useState, useEffect } from 'react';
import {
      CButton,
      CModal,
      CModalHeader,
      CModalBody,
      CModalFooter,
      CForm,
      CFormInput,
      CFormLabel,
      CFormFeedback,
      CCard,
      CCardBody,
      CCardImage,
      CRow,
      CCol
} from '@coreui/react';
import { toast } from 'react-toastify';
import MarketplaceService from '../services/MarketplaceService';
import 'react-toastify/dist/ReactToastify.css';
import Logger from 'src/utils/Logger';
import config from '../utils/EnvironmentSwitcher';

const ListNFTModal = ({ show, onHide, nft, onSuccess }) => {
      const [price, setPrice] = useState(nft?.price || 0);
      const [isSubmitting, setIsSubmitting] = useState(false);
      const [errors, setErrors] = useState({});
      const marketplaceService = new MarketplaceService();

      useEffect(() => {
            Logger.debug('ListNFTModal - NFT data received:', nft);
      }, [nft]);

      useEffect(() => {
            if (nft?.price) {
                  setPrice(nft.price);
            }
      }, [nft]);

      if (!show) return null;

      if (!nft) {
            Logger.error('ListNFTModal - NFT data is null');
            return (
                  <CModal visible={show} onClose={onHide}>
                        <CModalHeader closeButton>
                              <h5>Error</h5>
                        </CModalHeader>
                        <CModalBody>
                              <p>Unable to load NFT data. Please try again.</p>
                        </CModalBody>
                        <CModalFooter>
                              <CButton color="secondary" onClick={onHide}>
                                    Close
                              </CButton>
                        </CModalFooter>
                  </CModal>
            );
      }

      const validateForm = () => {
            const newErrors = {};
            // Allow empty string to be treated as 0, but require explicit value
            if (price === '' || price === null || price === undefined) {
                  newErrors.price = 'Price is required (use 0 for free listing)';
            } else {
                  // Validate that price is a valid number >= 0
                  const numPrice = Number(price);
                  if (isNaN(numPrice) || numPrice < 0) {
                        newErrors.price = 'Price must be a valid number >= 0';
                  }
            }
            if (!nft || !nft.nftId) {
                  newErrors.nft = 'Invalid NFT selected';
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
      };

      const updateStripeProductPrice = async (nftId, price) => {
            try {
                  const response = await fetch(`${config.paymentUrl}/mieros-product-update`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${localStorage.getItem('fbAccessToken')}`
                        },
                        body: JSON.stringify({
                              id: nftId,
                              price
                        })
                  });

                  if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Failed to update Stripe product');
                  }

                  return await response.json();
            } catch (error) {
                  console.error('Error updating Stripe product:', error);
                  throw error;
            }
      };

      const handleSubmit = async () => {
            if (!validateForm()) return;

            setIsSubmitting(true);
            try {
                  Logger.debug('Attempting to list NFT:', {
                        nftId: nft.nftId,
                        price: Number(price)
                  });

                  const response = await marketplaceService.listNFTForSale(
                        nft.nftId,
                        Number(price)
                  );

                  if (response) {
                        Logger.debug('NFT listed successfully:', response);
                        toast.success('NFT listed successfully');
                        if (onSuccess) onSuccess(response);
                        onHide();
                  }
            } catch (error) {
                  Logger.error('Error listing NFT:', error);
                  toast.error(error.message || 'Failed to list NFT');
            } finally {
                  setIsSubmitting(false);
            }
      };

      const renderNFTDetails = () => {
            Logger.info("nft", nft);
            const details = [
                  { label: 'Type', value: nft.itemType },
                  { label: 'Category', value: nft.itemCategory },
                  { label: 'Subject', value: nft.itemSubject },
                  { label: 'Media', value: nft.itemMedia },
                  { label: 'Date Created', value: new Date(nft.createdAt).toLocaleDateString() },
                  { label: 'Status', value: nft.status },
                  { label: 'Description', value: nft.itemDescription }
            ].filter(detail => detail.value);

            return (
                  <CRow className="g-3">
                        <CCol md={6}>
                              <CCard className="h-100">
                                    {nft.imageUrl ? (
                                          <CCardImage
                                                orientation="top"
                                                src={nft.imageUrl}
                                                style={{ 
                                                      objectFit: 'cover', 
                                                      height: '200px',
                                                      width: '100%'
                                                }}
                                                alt={nft.itemDetail}
                                          />
                                    ) : nft.image ? (
                                          <CCardImage
                                                orientation="top"
                                                src={`data:image/${nft.imageType};base64,${nft.image}`}
                                                style={{ 
                                                      objectFit: 'cover', 
                                                      height: '200px',
                                                      width: '100%'
                                                }}
                                                alt={nft.itemDetail}
                                          />
                                    ) : (
                                          <div className="text-center p-4">
                                                <p>No image available</p>
                                          </div>
                                    )}
                              </CCard>
                        </CCol>
                        <CCol md={6}>
                              <CCard className="h-100">
                                    <CCardBody>
                                          <h5 className="mb-3">{nft.itemDetail}</h5>
                                          {details.map((detail, index) => (
                                                <div key={index} className="mb-2">
                                                      <small className="mb-2">{detail.label}</small>
                                                      <div>{detail.value}</div>
                                                </div>
                                          ))}
                                    </CCardBody>
                              </CCard>
                        </CCol>
                  </CRow>
            );
      };

      return (
            <CModal visible={show} onClose={onHide} size="lg">
                  <CModalHeader closeButton>
                        <h5 className="modal-title">Confirm NFT Listing</h5>
                  </CModalHeader>
                  <CModalBody>
                        {renderNFTDetails()}
                        
                        <CForm className="mt-4">
                              <div className="mb-3">
                                    <CFormLabel>Set Listing Price (MiEROS Tokens) *</CFormLabel>
                                    <CFormInput
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={price}
                                          onChange={(e) => {
                                                setPrice(e.target.value);
                                                if (errors.price) {
                                                      setErrors({ ...errors, price: '' });
                                                }
                                          }}
                                          invalid={!!errors.price}
                                          required
                                          disabled={nft?.priceIsFixed}
                                    />
                                    {errors.price && (
                                          <CFormFeedback invalid>{errors.price}</CFormFeedback>
                                    )}
                                    <small className="mb-2">
                                          {Number(price) === 0 
                                                ? 'Free listing - Purchase will transfer Mieros tokens only (no USD payment required)'
                                                : 'Price in MiEROS tokens. USD payment will be processed via Stripe.'}
                                    </small>
                              </div>
                        </CForm>
                  </CModalBody>
                  <CModalFooter>
                        <CButton
                              color="secondary"
                              onClick={onHide}
                        >
                              Cancel
                        </CButton>
                        <CButton
                              color="primary"
                              disabled={isSubmitting}
                              onClick={handleSubmit}
                        >
                              {isSubmitting ? 'Listing...' : 'Confirm Listing'}
                        </CButton>
                  </CModalFooter>
            </CModal>
      );
};

export default ListNFTModal; 

